// Filtres « En retard » / « Urgentes » (WKF-013 §7.2) — deux bascules composables
// partagées par la page ET le widget cloche. Filtrage SERVEUR (param overdue +
// priority=URGENT) ; état persisté par l'appelant (user_preferences). DS : tokens
// destructive quand actif, texte toujours présent (a11y), aucun vert.

import { useTranslation } from 'react-i18next'
import { AlarmClock, AlertTriangle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

interface NotificationFiltersProps {
    overdue: boolean
    urgent: boolean
    onOverdue: (v: boolean) => void
    onUrgent: (v: boolean) => void
    /** Affiche le lien « Réinitialiser » quand un filtre est actif (page). */
    showReset?: boolean
    /** Taille compacte (widget cloche). */
    compact?: boolean
}

export function NotificationFilters({
    overdue,
    urgent,
    onOverdue,
    onUrgent,
    showReset = false,
    compact = false,
}: NotificationFiltersProps) {
    const { t } = useTranslation()
    // Hover NEUTRE gris (annule le `hover:bg-accent` teal du variant outline en mode
    // couleur Argalis) → transition blanc → gris clair → rouge, plus élégante que
    // blanc → vert → rouge (retour PO). État actif = destructive, et RESTE rouge au
    // survol (pas de retour au gris). twMerge garde nos `hover:*` (déclarés en dernier).
    const stateClasses = [
        'hover:bg-muted hover:text-foreground',
        'data-[state=on]:border-destructive/30 data-[state=on]:bg-destructive-bg data-[state=on]:text-destructive',
        'data-[state=on]:hover:bg-destructive-bg data-[state=on]:hover:text-destructive',
    ].join(' ')
    const itemClass = compact ? `h-8 gap-1 px-2 text-xs ${stateClasses}` : `gap-1.5 ${stateClasses}`
    const iconClass = compact ? 'h-3.5 w-3.5' : 'h-4 w-4'

    return (
        <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
                type="multiple"
                value={[overdue ? 'overdue' : '', urgent ? 'urgent' : ''].filter(Boolean)}
                onValueChange={(vals) => {
                    const o = vals.includes('overdue')
                    const u = vals.includes('urgent')
                    if (o !== overdue) onOverdue(o)
                    if (u !== urgent) onUrgent(u)
                }}
                className="gap-2"
            >
                <ToggleGroupItem
                    value="overdue"
                    variant="outline"
                    size={compact ? 'sm' : 'default'}
                    aria-label={t('notifications.filters.overdue', 'En retard')}
                    className={itemClass}
                >
                    <AlarmClock className={iconClass} />
                    {t('notifications.filters.overdue', 'En retard')}
                </ToggleGroupItem>
                <ToggleGroupItem
                    value="urgent"
                    variant="outline"
                    size={compact ? 'sm' : 'default'}
                    aria-label={t('notifications.filters.urgent', 'Urgentes')}
                    className={itemClass}
                >
                    <AlertTriangle className={iconClass} />
                    {t('notifications.filters.urgent', 'Urgentes')}
                </ToggleGroupItem>
            </ToggleGroup>
            {showReset && (overdue || urgent) && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={() => {
                        onOverdue(false)
                        onUrgent(false)
                    }}
                >
                    {t('notifications.filters.reset', 'Réinitialiser les filtres')}
                </Button>
            )}
        </div>
    )
}
